import { Link } from "react-router";
import { RouteFactory } from "@app/routePaths";
import iconPlus from "@assets/icons/icon-plus.svg";
import AccordionV2 from "@components/Accordion/AccordionV2";
import { useHandleUserAction } from "@features/mySph/hooks/useHandleUserAction";
import { MyAccountMyBTAccordionProps } from "@routes/MyAccount/utils/types";

export default function MyAccountMyBtPageEmptyState(): React.ReactElement {
  const { handleUserLogin } = useHandleUserAction();

  const handleOnSetSessionMyBT = () => {
    sessionStorage.setItem("from-account-page", "true");
  };

  return (
    <div data-testid="my-account-my-bt-page-empty-state-component">
      <div className="relative mb-15 h-auto w-full bg-gray-850 bg-[url('/src/app/assets/my-account/my-account-empty-state-banner.jpg')] bg-cover bg-[80%_100%] lg:bg-[70%_100%]">
        <div className="p-8">
          <p className="mb-3 max-w-[200px] font-poppins text-xl font-semibold leading-tight text-white md:max-w-[290px] md:text-7xl">
            Follow keywords. Own your news.
          </p>

          <Link
            to={RouteFactory.myBTOnboard}
            reloadDocument
            className="inline-flex w-max items-center rounded-[50px] bg-white px-6 py-2 text-center text-xs"
            onClick={handleOnSetSessionMyBT}
            aria-label="myBTOnboard"
          >
            <img src={iconPlus} width={24} height={24} alt="add keyword" />
            <p className="leading-0 font-poppins text-xs font-bold tracking-2%">
              Add keyword
            </p>
          </Link>
        </div>
      </div>

      <AccordionV2
        testId="my-account-my-bt-accordion"
        headerChildren={
          <MyAccountMyBTPageAccordionHeader title="What is myBT?" />
        }
      >
        <div className="font-xs pb-5 font-public-sans text-gray-850">
          <p>
            myBT is a feature exclusive to registered users and subscribers of
            The Business Times (BT).
          </p>

          <p>
            They can customise their daily reading experience with keywords and
            get curated content through their personalised myBT pages on the BT
            website and app.
          </p>
        </div>
      </AccordionV2>

      <AccordionV2
        testId="my-account-my-bt-accordion"
        className="pt-6"
        headerChildren={
          <MyAccountMyBTPageAccordionHeader title="Is myBT available to everyone?" />
        }
      >
        <div className="font-xs pb-5 font-public-sans text-gray-850">
          <p>
            {
              "myBT is a feature exclusive to BT's registered users and subscribers."
            }
            <button
              className="mr-2 text-verticals-btblue"
              onClick={() => {
                handleUserLogin();
              }}
            >
              Create a mySPH account
            </button>
            {
              "to become a registered user. Once you've registered, you can log in to BT's website or app to access the content and follow keywords."
            }
          </p>

          <p>
            You can also use social logins. If you have a Google or Facebook
            account, you can use myBT after signing in with your own
            credentials. We will create a mySPH account for you automatically.
          </p>

          <p>
            {
              "To access myBT on the BT app, go to the Google Play Store or Apple's App Store to download the latest version of the app."
            }
          </p>
        </div>
      </AccordionV2>

      <AccordionV2
        testId="my-account-my-bt-accordion"
        className="pt-6"
        headerChildren={
          <MyAccountMyBTPageAccordionHeader title="How many keywords can I follow?" />
        }
      >
        <div className="font-xs pb-5 font-public-sans text-gray-850">
          <p>Registered users can select up to three keywords to follow.</p>

          <p>
            BT subscribers can select up to 30 keywords, for a more curated
            reading experience.
          </p>
        </div>
      </AccordionV2>

      <AccordionV2
        testId="my-account-my-bt-accordion"
        className="pt-6"
        headerChildren={
          <MyAccountMyBTPageAccordionHeader title="How much does myBT cost?" />
        }
      >
        <div className="font-xs pb-5 font-public-sans text-gray-850">
          <p>
            {
              "The feature is free for registered users. However, registered users who haven't bought a BT subscription will have limited access to BT content and may only follow three keywords."
            }
          </p>

          <p>
            Buying a BT subscription unlocks full access to BT content and the
            ability to follow up to 30 keywords, for a more customised
            experience.
          </p>
        </div>
      </AccordionV2>
    </div>
  );
}

export function MyAccountMyBTPageAccordionHeader({
  title,
}: MyAccountMyBTAccordionProps): React.ReactElement {
  return (
    <p className="mb-0 font-poppins text-xs font-semibold tracking-2%">
      {title}
    </p>
  );
}
